import { Theme, useTheme } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTodos } from '../../../../context/TodoContext/TodoContext';
import { Todo } from "../../../../types/types";
import { DayHeader, DayNumber, EventsContainer, StyledDayCell } from '../../styles/styles';
import { AddTodoOverlay } from "./components/AddTodoOverlay";
import { EditTodoField } from './components/EditTodoField';
import { TodoList } from "./components/TodoList";
import { useNavigate } from "react-router-dom";

interface CalendarDayProps {
    day: dayjs.Dayjs;
    isCurrentMonth: boolean;
    isSelected: boolean;
    onTodoClick: (todo: Todo) => void;
    getTodoColor: (todo: Todo, theme: Theme) => string | undefined;
    isAddMode: boolean;
    setAddMode: (addMode: boolean) => void;
    onAddClick: () => void;
    editingDay: Dayjs | null;
    setEditingDay: (editingDay: Dayjs | null) => void;
    onSaveTodo: (date: Dayjs, text: string) => Promise<Todo | null> | undefined;
    getTodosForDate: (day: Dayjs) => Todo[];
    showTime: boolean;
}

const CalendarDay: React.FC<CalendarDayProps> = ({
    day,
    isCurrentMonth,
    isSelected,
    onTodoClick,
    getTodoColor,
    isAddMode,
    setAddMode,
    editingDay,
    setEditingDay,
    onAddClick,
    onSaveTodo,
    getTodosForDate,
    showTime
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { todos, activeWorkspace } = useTodos();
    const [dayTodos, setDayTodos] = useState<Todo[]>(getTodosForDate(day));
    const isToday = day.isSame(dayjs(), 'day');
    const [editingText, setEditingText] = useState<string>('');
    const isEditing = editingDay?.isSame(day, 'day') || false;

    useEffect(() => {
        setDayTodos(getTodosForDate(day));
    }, [todos, day, getTodosForDate]);

    const handleAddClick = () => {
        onAddClick();
        setEditingDay(day);
    };

    const handleSaveTodo = async () => {
        if (editingText.trim()) {
            const todo = await onSaveTodo(day, editingText);
            if (todo) navigate(`/calendar/${activeWorkspace}/${todo.id}`, { replace: true });
        }
        resetEditState();
    };

    const resetEditState = () => {
        setAddMode(false);
        setEditingDay(null);
        setEditingText('');
    };

    return (
        <StyledDayCell
            isCurrentMonth={isCurrentMonth}
            isToday={isToday}
            isSelected={isSelected}
        >
            <DayHeader>
                <DayNumber isToday={isToday} isCurrentMonth={isCurrentMonth}>
                    {day.date()}
                </DayNumber>
            </DayHeader>

            <EventsContainer>
                {isEditing && (
                    <EditTodoField
                        editingText={editingText}
                        setEditingText={setEditingText}
                        onSave={handleSaveTodo}
                        getTodoColor={getTodoColor}
                        theme={theme}
                    />
                )}

                <TodoList
                    todos={dayTodos}
                    getTodoColor={getTodoColor}
                    onTodoClick={onTodoClick}
                    showTime={showTime}
                    theme={theme}
                />

                {isAddMode && !editingDay && isCurrentMonth && (
                    <AddTodoOverlay onClick={handleAddClick} />
                )}
            </EventsContainer>
        </StyledDayCell>
    );
};

export default CalendarDay; 